import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

type ProfileType = 'listing' | 'trainer' | 'nutritionist' | 'physiotherapist' | 'event';

interface UseProfileMetricsConfig {
  type: ProfileType;
  profileId: string;
  profileUserId?: string;
  views?: number; // For listing type fallback
}

interface ContactClicksBreakdown {
  phone: number;
  email: number;
  website: number;
  total: number;
}

interface ProfileMetrics {
  profileViews: number;
  views: number;
  searchAppearances: number;
  uniqueVisitors: number;
  contactClicks: ContactClicksBreakdown;
  conversionRate: string;
  shares?: number;
  registrations?: number;
}

// Helper to get total contact clicks as a number
export const getContactClicksTotal = (clicks: ContactClicksBreakdown): number => {
  return clicks.total;
};

export const useProfileMetrics = ({ type, profileId, profileUserId, views = 0 }: UseProfileMetricsConfig) => {
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEventOwner, setIsEventOwner] = useState(false);
  const [metrics, setMetrics] = useState<ProfileMetrics>({
    profileViews: 0,
    views: 0,
    searchAppearances: 0,
    uniqueVisitors: 0,
    contactClicks: { phone: 0, email: 0, website: 0, total: 0 },
    conversionRate: '0.0'
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setCurrentUserId(user?.id || null);

        if (!profileId) {
          setLoading(false);
          return;
        }

        switch (type) {
          case 'listing':
            await fetchListingMetrics(profileId, user?.id === profileUserId, views);
            break;
          case 'trainer':
            await fetchTrainerMetrics(profileId);
            break;
          case 'nutritionist':
            await fetchNutritionistMetrics(profileId);
            break;
          case 'physiotherapist':
            await fetchPhysiotherapistMetrics(profileId);
            break;
          case 'event':
            await fetchEventMetrics(profileId, user?.id || null);
            break;
        }
      } catch (error) {
        console.error(`Error fetching ${type} metrics:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [type, profileId, profileUserId, views]);

  const fetchListingMetrics = async (id: string, isOwner: boolean, fallbackViews: number) => {
    const { data } = await supabase
      .from('listings')
      .select('profile_views, search_appearances, unique_visitors, contact_clicks')
      .eq('id', id)
      .maybeSingle();

    // Fetch share count
    const { count: shareCount } = await supabase
      .from('listing_social_shares')
      .select('*', { count: 'exact', head: true })
      .eq('listing_id', id);

    // Fetch contact clicks breakdown if owner
    let contactClicksBreakdown = { phone: 0, email: 0, website: 0, total: 0 };
    if (isOwner) {
      const { data: clicks } = await supabase
        .from('listing_contact_clicks')
        .select('click_type')
        .eq('listing_id', id);

      if (clicks) {
        contactClicksBreakdown = {
          phone: clicks.filter(c => c.click_type === 'phone').length,
          email: clicks.filter(c => c.click_type === 'email').length,
          website: clicks.filter(c => c.click_type === 'website').length,
          total: clicks.length
        };
      }
    }

    const profileViews = data?.profile_views || fallbackViews || 0;
    const totalContacts = contactClicksBreakdown.total || data?.contact_clicks || 0;
    
    setMetrics({
      profileViews,
      views: profileViews,
      searchAppearances: data?.search_appearances || 0,
      uniqueVisitors: data?.unique_visitors || 0,
      contactClicks: {
        ...contactClicksBreakdown,
        total: totalContacts
      },
      conversionRate: profileViews > 0 ? ((totalContacts / profileViews) * 100).toFixed(1) : '0.0',
      shares: shareCount || 0
    });
  };

  const fetchTrainerMetrics = async (id: string) => {
    const { data } = await supabase
      .from('trainer_profiles')
      .select('profile_views, search_appearances, unique_visitors, contact_clicks')
      .eq('id', id)
      .maybeSingle();

    if (data) {
      const profileViews = data.profile_views || 0;
      const contactClicks = data.contact_clicks || 0;
      setMetrics({
        profileViews,
        views: profileViews,
        searchAppearances: data.search_appearances || 0,
        uniqueVisitors: data.unique_visitors || 0,
        contactClicks: { phone: 0, email: 0, website: 0, total: contactClicks },
        conversionRate: profileViews > 0 ? ((contactClicks / profileViews) * 100).toFixed(1) : '0.0'
      });
    }
  };

  const fetchNutritionistMetrics = async (id: string) => {
    const { data } = await supabase
      .from('nutritionist_profiles')
      .select('profile_views, search_appearances, unique_visitors, contact_clicks')
      .eq('id', id)
      .maybeSingle();

    if (data) {
      const profileViews = data.profile_views || 0;
      const contactClicks = data.contact_clicks || 0;
      setMetrics({
        profileViews,
        views: profileViews,
        searchAppearances: data.search_appearances || 0,
        uniqueVisitors: data.unique_visitors || 0,
        contactClicks: { phone: 0, email: 0, website: 0, total: contactClicks },
        conversionRate: profileViews > 0 ? ((contactClicks / profileViews) * 100).toFixed(1) : '0.0'
      });
    }
  };

  const fetchPhysiotherapistMetrics = async (id: string) => {
    const { data } = await supabase
      .from('physiotherapist_profiles')
      .select('profile_views, search_appearances, unique_visitors, contact_clicks')
      .eq('id', id)
      .maybeSingle();

    if (data) {
      const profileViews = data.profile_views || 0;
      const contactClicks = data.contact_clicks || 0;
      setMetrics({
        profileViews,
        views: profileViews,
        searchAppearances: data.search_appearances || 0,
        uniqueVisitors: data.unique_visitors || 0,
        contactClicks: { phone: 0, email: 0, website: 0, total: contactClicks },
        conversionRate: profileViews > 0 ? ((contactClicks / profileViews) * 100).toFixed(1) : '0.0'
      });
    }
  };

  const fetchEventMetrics = async (eventId: string, userId: string | null) => {
    const { data: event } = await supabase
      .from("events")
      .select("organizer_id, current_participants")
      .eq("id", eventId)
      .maybeSingle();

    const { count: shares } = await supabase
      .from("listing_social_shares")
      .select("*", { count: "exact", head: true })
      .eq("listing_id", eventId);

    const { data: analyticsData } = await supabase
      .from('analytics_events')
      .select('type, metadata')
      .eq('metadata->>event_id', eventId);

    const viewCount = analyticsData?.filter(e => e.type === 'page_view').length || 0;
    const registrations = event?.current_participants || 0;

    setMetrics({
      profileViews: viewCount,
      views: viewCount,
      searchAppearances: 0,
      uniqueVisitors: 0,
      contactClicks: { phone: 0, email: 0, website: 0, total: 0 },
      conversionRate: viewCount > 0 ? ((registrations / viewCount) * 100).toFixed(1) : '0.0',
      shares: shares || 0,
      registrations
    });

    if (event && userId) {
      setIsEventOwner(event.organizer_id === userId);
    }
  };

  const isOwner = type === 'event' ? isEventOwner : currentUserId === profileUserId;
  const isAuthenticated = !!currentUserId;

  return {
    loading,
    isOwner,
    isAuthenticated,
    currentUserId,
    navigate,
    metrics
  };
};
